/*!
=========================================================
* Ziro AI Studio - ZiroChat Component
=========================================================
* Reusable chat interface component for Studio pages
*/

import React, { useState, useRef, useEffect } from "react";
import { Button, Spinner, Card, CardHeader, CardBody, Row, Col } from "reactstrap";
import "./StudioChat.css";
import ziroChatInterfaces from "../../services/ziroChatInterfaces";

const ZiroChat = ({
    project,
    projectCode,
    isLoading: projectLoading,
    isAuthenticated,
    onToggleEditor,
    onSaveCode,
    isSaving,
    isLoggedOut = false
}) => {
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [useGPT4, setUseGPT4] = useState(true); // Default to GPT-4
    const [error, setError] = useState(null);
    const chatboxRef = useRef(null);
    const [hasShownLogoutMessage, setHasShownLogoutMessage] = useState(false);
    
    // Add system message when logged out
    useEffect(() => {
        if (isLoggedOut && !hasShownLogoutMessage) {
            setChatMessages(prev => [...prev, {
                sender: 'system',
                text: '🔒 Session Expired\n\nYou have been logged out from another window. Please log in again to continue chatting.'
            }]);
            setHasShownLogoutMessage(true);
        }
    }, [isLoggedOut, hasShownLogoutMessage]);

    // Initialize thread and welcome message when project loads
    useEffect(() => {
        const initializeChat = async () => {
            if (!project) return;
            
            // Make helper functions available to production chat.js
            window.getEditorCode = () => project.content || project.code || '';
            window.getCodeLink = () => window.location.href;
            window.getProjectId = () => project.code || project.file_id || projectCode || '';
            window.getProjectOwnerId = () => project.user_id || '';
            window.getCohortId = () => localStorage.getItem('cohort_id') || '';
            window.getProjectTitle = () => project.title || '';
            
            // getCookie function (required by production chat.js)
            window.getCookie = (cname) => {
                const name = cname + "=";
                const decodedCookie = decodeURIComponent(document.cookie);
                const ca = decodedCookie.split(';');
                for(let i = 0; i < ca.length; i++) {
                    let c = ca[i];
                    while (c.charAt(0) === ' ') {
                        c = c.substring(1);
                    }
                    if (c.indexOf(name) === 0) {
                        return c.substring(name.length, c.length);
                    }
                }
                return "";
            };
            
            // Mock UI feedback functions used by production chat.js
            // We handle UI in React, but production code calls these
            window.addStatusMessage = (text) => {
                console.log('📊 Status:', text);
                // Could add to chat messages if needed
            };
            
            window.addErrorStatusMessage = (text) => {
                console.error('⚠️ Error Status:', text);
            };
            
            window.addErrorMessage = (text) => {
                console.error('❌ Error:', text);
                // Could add error to chat messages if needed
            };
            
            window.getRandomErrorMessage = () => {
                const messages = [
                    'Oops! Something went wrong',
                    'Houston, we have a problem',
                    'Error encountered',
                    'Something unexpected happened'
                ];
                return messages[Math.floor(Math.random() * messages.length)];
            };
            
            // Create mock DOM elements that chat.js expects (but we handle in React)
            if (!document.querySelector("#api_check_container")) {
                const mockApiCheck = document.createElement('input');
                mockApiCheck.id = "api_check_container";
                mockApiCheck.type = "checkbox";
                mockApiCheck.checked = useGPT4;
                mockApiCheck.style.display = "none";
                document.body.appendChild(mockApiCheck);
            }
            if (!document.querySelector("#check_container")) {
                const mockSocCheck = document.createElement('input');
                mockSocCheck.id = "check_container";
                mockSocCheck.type = "checkbox";
                mockSocCheck.checked = false; // Socratic mode off by default
                mockSocCheck.style.display = "none";
                document.body.appendChild(mockSocCheck);
            }
            
            // Set all global variables that production chat.js expects
            // Production updateToolbarButtonVisibility() will use these to show/hide buttons
            window.globalFileId = project.code || project.file_id || projectCode || '';
            window.globalUserId = localStorage.getItem('google_user_id') || '';
            window.globalProjectOwnerId = project.user_id || '';
            window.globalProjectTitle = project.title || '';
            window.globalProjectCohortId = project.cohort_id || undefined;
            window.globalProjectAssistantId = project.assistant_id || undefined;
            window.globalCohortId = localStorage.getItem('cohort_id') || undefined;
            window.globalOtherTags = project.other_tags || '';
            window.globalNoAnnotations = false; // Default setting
            
            // Call production updateToolbarButtonVisibility after DOM is ready
            // This function (from ziro_studio.html) handles all button visibility logic
            setTimeout(() => {
                if (typeof window.updateToolbarButtonVisibility === 'function') {
                    window.updateToolbarButtonVisibility();
                }
            }, 100);
            
            console.log('🔧 Set project context for chat.js:', {
                globalFileId: window.globalFileId,
                globalUserId: window.globalUserId,
                globalProjectTitle: window.globalProjectTitle,
                globalProjectCohortId: window.globalProjectCohortId,
                globalCohortId: window.globalCohortId,
                projectCode: projectCode,
                hasContent: !!(project.content || project.code)
            });
            
            // Add welcome message (only if project is loaded and no messages yet)
            if (project && project.title && chatMessages.length === 0) {
                setChatMessages([
                    {
                        sender: 'bot',
                        text: `Hello, I am Ziro! How may I help you with "${project.title}" today?`
                    }
                ]);
            }

            // Initialize OpenAI thread using production chat.js
            try {
                await ziroChatInterfaces.initializeThread();
                const threadId = ziroChatInterfaces.getThreadId();
                console.log('✅ Thread initialized:', threadId);
            } catch (error) {
                console.error('Error initializing thread:', error);
            }
        };

        initializeChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, projectCode]);
    
    // Update toolbar button visibility and auth state when authentication changes
    // This ensures buttons show/hide when user logs in on an unauthenticated page
    // CRITICAL: Also updates ZIRO_LOGGED_IN_USER_PROFILE_EXPIRY_TS to prevent "Session expired" errors
    useEffect(() => {
        if (!project) return; // Need project data first
        
        // Update global variables from localStorage (they may have changed after login)
        window.globalUserId = localStorage.getItem('google_user_id') || '';
        
        // CRITICAL: Call fetchProfileAndCheckAuthorization to update expiry timestamp
        // This prevents "Session expired" errors after logging in during an active chat
        if (typeof window.fetchProfileAndCheckAuthorization === 'function') {
            console.log('🔄 Calling fetchProfileAndCheckAuthorization to update auth state and expiry timestamp');
            window.fetchProfileAndCheckAuthorization().then(() => {
                console.log('✅ Auth state updated, expiry timestamp refreshed');
                
                // After updating auth state, call fetchProfile for toolbar button visibility
                if (typeof window.fetchProfile === 'function') {
                    return window.fetchProfile();
                }
            }).then(() => {
                console.log('✅ Profile fetched, buttons should be updated');
            }).catch(err => {
                console.error('Error updating auth state:', err);
                // Fallback: try to call updateToolbarButtonVisibility anyway
                if (typeof window.updateToolbarButtonVisibility === 'function') {
                    window.updateToolbarButtonVisibility();
                }
            });
        } else if (typeof window.fetchProfile === 'function') {
            // Fallback: use fetchProfile if fetchProfileAndCheckAuthorization is not available
            console.log('🔄 Calling production fetchProfile to update admin status and buttons');
            window.fetchProfile().then(() => {
                console.log('✅ Profile fetched, buttons should be updated');
            }).catch(err => {
                console.error('Error fetching profile:', err);
                // Fallback: try to call updateToolbarButtonVisibility anyway
                if (typeof window.updateToolbarButtonVisibility === 'function') {
                    window.updateToolbarButtonVisibility();
                }
            });
        } else {
            // Final fallback: call updateToolbarButtonVisibility directly
            const timeoutId = setTimeout(() => {
                if (typeof window.updateToolbarButtonVisibility === 'function') {
                    console.log('🔄 Calling updateToolbarButtonVisibility after auth change');
                    window.updateToolbarButtonVisibility();
                }
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [isAuthenticated, project]); // Trigger when auth state or project changes

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (chatboxRef.current) {
            chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
        }
    }, [chatMessages]);

    const handleSendMessage = async () => {
        // Prevent sending if not authenticated (logout occurred)
        if (!isAuthenticated) {
            console.warn("❌ Cannot send message: User is not authenticated");
            setError("Please log in to send messages.");
            return;
        }
        
        if (!chatInput.trim() || isSending || !project) return;

        const userMessage = chatInput.trim();
        setChatInput("");
        setError(null);

        // Add user message to UI immediately
        setChatMessages(prev => [...prev, {
            sender: 'user',
            text: userMessage
        }]);

        setIsSending(true);

        try {
            console.log('📤 Sending message using production chat.js:', {
                message: userMessage,
                projectCode: project.code,
                useGPT4
            });

            // CRITICAL: Refresh auth state before sending message to prevent "Session expired" errors
            // This is especially important if user logged in during an active chat session
            if (typeof window.fetchProfileAndCheckAuthorization === 'function') {
                console.log('🔄 Refreshing auth state before sending message...');
                try {
                    await window.fetchProfileAndCheckAuthorization();
                    console.log('✅ Auth state refreshed before message send');
                } catch (authError) {
                    console.warn('⚠️ Auth refresh failed, proceeding anyway:', authError);
                    // Continue even if auth refresh fails - getBotResponse will handle it
                }
            }

            // Set GPT-4 toggle in global state and mock DOM element
            if (window.OPENAI_USE_UPGRADED_MODEL !== undefined) {
                window.OPENAI_USE_UPGRADED_MODEL = useGPT4;
            }
            const apiCheckElement = document.querySelector("#api_check_container");
            if (apiCheckElement) {
                apiCheckElement.checked = useGPT4;
            }

            // Call production getBotResponse function
            const response = await ziroChatInterfaces.sendMessage(userMessage);

            // Add AI response to chat
            if (response) {
                setChatMessages(prev => [...prev, {
                    sender: 'bot',
                    text: response
                }]);
                console.log('✅ Response received:', response.substring(0, 100) + '...');
            } else {
                throw new Error('No response received from AI');
            }
        } catch (error) {
            console.error('❌ Error sending message:', error);
            
            // Extract error message from various possible formats
            let errorMessage = 'Sorry, there was an error processing your message. Please try again.';
            
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.error) {
                errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
            } else if (error?.data?.message) {
                errorMessage = error.data.message;
            }
            
            console.error('📋 Error message:', errorMessage);
            
            setError(errorMessage);
            setChatMessages(prev => [...prev, {
                sender: 'bot',
                text: `⚠️ ${errorMessage}`
            }]);
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Card>
            <CardHeader>
                <Row>
                    <Col>
                        <h3 id="projectTitle" className="title d-inline" title="Project Info/Login Page">
                            {projectLoading ? (
                                <>
                                    <Spinner size="sm" color="light" />
                                    Loading project...
                                </>
                            ) : (
                                project?.title || 'Studio'
                            )}
                        </h3>
                    </Col>
                    <Col md="auto">
                        <div id="button-toolbar">
                    {/* Code Editor toggle - hidden by default in production */}
                    {isAuthenticated && (
                        <Button
                            id="ace_editor_toggle_btn"
                            className="button button_toolbar green-button"
                            onClick={onToggleEditor}
                            title="Coach Context Editor"
                            size="sm"
                            style={{ display: 'none' }} // Match production: hidden by default
                        >
                            <i className="fa fa-code"></i>
                        </Button>
                    )}

                    {/* System Instructions button - hidden by default */}
                    {isAuthenticated && (
                        <Button
                            id="system_instruction_btn"
                            className="button button_toolbar green-button"
                            title="Assistant System Instructions"
                            size="sm"
                            style={{ display: 'none' }}
                        >
                            <i className="fa fa-user-circle"></i>
                        </Button>
                    )}

                    {/* Upload Web App button - admin only, hidden by default */}
                    <Button
                        id="upload-webapp"
                        className="button button_toolbar green-button"
                        title="Upload Web App HTML"
                        size="sm"
                        style={{ display: 'none' }}
                    >
                        <i className="fa fa-html5"></i>
                    </Button>

                    {/* Download Web App button - admin only, hidden by default */}
                    <Button
                        id="download-webapp"
                        className="button button_toolbar green-button"
                        onClick={() => {
                            if (typeof window.saveGlobalWebAppSourceToFile === 'function') {
                                window.saveGlobalWebAppSourceToFile();
                            }
                        }}
                        title="Download Web App HTML"
                        size="sm"
                        style={{ display: 'none' }}
                    >
                        <i className="fa fa-download"></i>
                    </Button>

                    {/* Open Web App button - admin only, hidden by default */}
                    <Button
                        id="open-webapp"
                        className="button button_toolbar green-button"
                        onClick={() => {
                            if (typeof window.openGlobalWebAppUrlInNewTab === 'function') {
                                window.openGlobalWebAppUrlInNewTab();
                            }
                        }}
                        title="Open Web App in New Tab"
                        size="sm"
                        style={{ display: 'none' }}
                    >
                        <i className="fa fa-external-link"></i>
                    </Button>

                    {/* Save button - hidden by default in production */}
                    {isAuthenticated && (
                        <Button
                            id="save_btn"
                            className="button button_toolbar green-button"
                            onClick={onSaveCode}
                            disabled={isSaving || !project}
                            title="Save Project"
                            size="sm"
                            style={{ display: 'none' }} // Match production: hidden by default
                        >
                            <i className="fa fa-save"></i>
                        </Button>
                    )}

                    {/* Project Camera toolbar button - hidden by default */}
                    {isAuthenticated && (
                        <Button
                            id="project-camera-toolbar"
                            className="button button_toolbar green-button"
                            title="Update Project Image"
                            size="sm"
                            style={{ display: 'none' }}
                        >
                            <i className="fa fa-camera"></i>
                        </Button>
                    )}

                    {/* User Validation button - hidden by default */}
                    <Button
                        id="show-user-validation-toolbar"
                        className="button button_toolbar green-button"
                        title="User Validation"
                        size="sm"
                        style={{ display: 'none' }}
                    >
                        <i className="fa fa-id-badge"></i>
                    </Button>

                    {/* Print button - ALWAYS VISIBLE (matches production) */}
                    <Button
                        id="print-convo-toolbar"
                        className="button button_toolbar green-button"
                        onClick={() => window.print()}
                        title="Print Conversation"
                        size="sm"
                    >
                        <i className="fa fa-print"></i>
                    </Button>

                    {/* Show Coach Context button - hidden by default */}
                    <Button
                        id="window_btn"
                        className="button button_window_toggle green-button"
                        title="Show Coach Context"
                        size="sm"
                        style={{ display: 'none' }}
                    >
                        <i className="fa fa-file-text-o"></i>
                    </Button>
                        </div>
                    </Col>
                </Row>
            </CardHeader>

            <CardBody>
                {/* Chat wrapper */}
                <div className="studio-chat-wrapper">
                <div className="chat-window-container">
                    <div className="chat-bar-collapsible">
                        <div className="chat-content">
                            <div className="full-chat-block">
                                <div className="outer-container">
                                    {/* Chat messages area */}
                                    <div className="chat-container" ref={chatboxRef}>
                                        <div id="chatbox" className="chatbox-class">
                                            {projectLoading ? (
                                                <div className="text-center loading-container">
                                                    <Spinner color="primary" />
                                                    <p className="loading-text">Loading project...</p>
                                                </div>
                                            ) : (
                                                chatMessages.map((msg, index) => (
                                                    <div
                                                        key={index}
                                                        className={`message-bubble ${
                                                            msg.sender === 'user' ? 'user-message' : 
                                                            msg.sender === 'system' ? 'system-message' : 
                                                            'bot-message'
                                                        }`}
                                                        style={
                                                            msg.sender === 'system' ? {
                                                                color: '#ff9800',
                                                                fontStyle: 'italic',
                                                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                                                borderLeft: '3px solid #ff9800',
                                                                padding: '12px',
                                                                margin: '10px 0'
                                                            } : {}
                                                        }
                                                    >
                                                        <div style={{
                                                            whiteSpace: 'pre-wrap'
                                                        }}>{msg.text}</div>
                                                    </div>
                                                ))
                                            )}
                                            {isSending && (
                                                <div className="message-bubble bot-message">
                                                    <div className="loading-spinner"></div>
                                                    <span className="thinking-message">Thinking...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div id="chat-bar-bottom">
                                        <p></p>
                                    </div>

                                    {/* Input area */}
                                    <div className="chat-bar-input-block">
                                        {/* Action buttons - Match production visibility */}
                                        <div className="button-container">
                                            <div className="button-row">
                                                {/* Copy convo - hidden in production */}
                                                <button
                                                    id="copy-convo"
                                                    title="Copy entire conversation to clipboard(Shift - Print PDF)"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-files-o"></i>
                                                </button>
                                                {/* Conversation history - hidden in production (but conditionally shown) */}
                                                <button
                                                    id="convo-history"
                                                    title="Get past conversation history(Shift - Get Prompt History)"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-comments-o"></i>
                                                </button>
                                            </div>
                                            <div className="button-row">
                                                {/* Agent action - VISIBLE in production */}
                                                <button
                                                    id="agent-action"
                                                    title="Attach AI Agents"
                                                >
                                                    <i className="fa fa-plus-circle"></i>
                                                </button>
                                                {/* Upload image - hidden in production */}
                                                <button
                                                    id="upload-image"
                                                    title="Upload File to Ziro(Shift - Show AI Agents)"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-paperclip"></i>
                                                </button>
                                                {/* Project camera - hidden in production */}
                                                <button
                                                    id="project-camera"
                                                    title="Update Project Image"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-camera"></i>
                                                </button>
                                                {/* Upload PDF - hidden in production */}
                                                <button
                                                    id="upload-pdf"
                                                    title="Upload PDF File to Ziro"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-paperclip"></i>
                                                </button>
                                                {/* Hamburger menu - hidden in production */}
                                                <button
                                                    id="hamburger-menu"
                                                    title="Open menu"
                                                    style={{ display: 'none' }}
                                                >
                                                    <i className="fa fa-bars"></i>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Socratic toggle - hidden in production */}
                                        <div className="socratic-toggle-container">
                                            <span className="soc-label">Socratic</span>
                                            <div className="slider-parent">
                                                <div className="soc-class">
                                                    <label htmlFor="socratic-toggle" className="switch">
                                                        <input type="checkbox" id="socratic-toggle" />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* GPT4 toggle - VISIBLE in production */}
                                        <div className="gpt4-toggle-container">
                                            <span className="api-label">GPT4</span>
                                            <div className="slider-parent">
                                                <div className="soc-class">
                                                    <label htmlFor="gpt4-toggle" className="switch">
                                                        <input
                                                            type="checkbox"
                                                            id="gpt4-toggle"
                                                            checked={useGPT4}
                                                            onChange={(e) => setUseGPT4(e.target.checked)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Message input */}
                                        <div className="input-wrapper">
                                            <div id="userInput">
                                                <textarea
                                                    id="textInput"
                                                    className="input-box"
                                                    placeholder="Type your message..."
                                                    value={chatInput}
                                                    onChange={(e) => setChatInput(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    disabled={isSending || projectLoading || !isAuthenticated}
                                                    rows={1}
                                                />
                                            </div>
                                            <Button
                                                id="sendButton"
                                                onClick={handleSendMessage}
                                                disabled={isSending || projectLoading || !chatInput.trim() || !isAuthenticated}
                                                title="Send message (or press Enter)"
                                            >
                                                <i className="fa fa-paper-plane"></i>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </CardBody>
        </Card>
    );
};

export default ZiroChat;
